/*
 * ----------------------------------------------------------------------------------------------------------------
 * CPO BATS GRID FILTER
 * ----------------------------------------------------------------------------------------------------------------
 *
 * Client-side certification/length filter for sections/cpo-bats-grid.liquid. Its own asset file rather than an
 * addition to custom.js, following the same isolation precedent as assets/bbcor-bats-grid-filter.js and
 * assets/swatch-swap.js.
 *
 * DELIBERATELY SIMPLER THAN THE BBCOR/USA FILTER. Those files carry three card types ('line', 'teeball',
 * 'hide-on-filter') and a swatch-narrowing pass, because those pages collapse multi-edition bat lines into a
 * single swatchable card and a length selection has to narrow which editions remain pickable inside a card
 * that is still visible. Certified Pre-Owned does not group editions — confirmed 2026-08-31, the Bonesaber
 * Hybrid colorway grouping used on the BBCOR and USA pages is explicitly not being applied here — so every
 * product renders as a plain card and filtering is purely hide/show. There is no swatch row to narrow, no
 * sibling handle map, and no default-edition swap.
 *
 * If edition grouping is ever added to this page, this file is NOT the place to bolt it on: lift the 'line'
 * card model and narrowSwatches() from bbcor-bats-grid-filter.js rather than reinventing them here.
 *
 * GROUP HEADINGS: cards are split into one headed product-list per certification (see the section file).
 * A group whose cards are all filtered out is hidden along with its heading — otherwise a 'USSSA' heading
 * would sit above empty space when someone filters to a length that group does not carry. See
 * updateGroupVisibility(). When grouping is switched off in the section settings there are simply no
 * [data-cpo-group] elements and that pass no-ops.
 *
 * TITLE PREFIX: optionally wraps a leading word in every card title (e.g. "PREOWNED") in a span so it can be
 * coloured. Done here, scoped to [data-cpo-card], rather than in 'bbcor-product-card' — that snippet renders
 * the BBCOR and USA grids too, and a shared-file edit has already broken Add to Cart on both pages once (see
 * bbcor-bats-grid-filter.js's header). Runs once at init; it is not part of the filter path.
 *
 * FACET DATA comes from a JSON script tag inside each card wrapper — see snippets/cpo-bat-card.liquid:
 *   { "certification": "BBCOR", "lengths": ["30", "32", "33", "33.5"] }
 *
 * Certification is product-level (product.metafields.custom.certification); lengths are collected across a
 * product's available variants only, so a bat whose 32" is sold out will not answer to the 32" filter.
 */

(function () {
	'use strict';

	function init() {
		var root = document.querySelector('[data-cpo-root]');
		if (!root) return;

		var cards = buildCardModel(root);
		var filterInputs = Array.prototype.slice.call(root.querySelectorAll('[data-cpo-filter]'));
		var emptyState = root.querySelector('[data-cpo-empty-state]');
		var clearButton = root.querySelector('[data-cpo-clear]');

		filterInputs.forEach(function (input) {
			input.addEventListener('change', function () {
				applyFilters(root, cards, filterInputs, emptyState);
			});
		});

		if (clearButton) {
			clearButton.addEventListener('click', function () {
				filterInputs.forEach(function (input) {
					input.checked = false;
				});
				applyFilters(root, cards, filterInputs, emptyState);
			});
		}

		highlightTitlePrefix(root);

		applyFilters(root, cards, filterInputs, emptyState);
	}

	// Split a leading word out of each card title into its own span so it can be styled. Matched
	// case-insensitively but the original casing is preserved in the output.
	function highlightTitlePrefix(root) {
		var prefix = (root.getAttribute('data-cpo-title-prefix') || '').trim();
		if (!prefix) return;

		var titles = root.querySelectorAll('[data-cpo-card] .product-title');

		Array.prototype.forEach.call(titles, function (el) {
			if (el.dataset.cpoPrefixed === 'true') return;

			var text = el.textContent.trim();
			if (text.slice(0, prefix.length).toLowerCase() !== prefix.toLowerCase()) return;

			var span = document.createElement('span');
			span.className = 'cpo-title-prefix';
			span.textContent = text.slice(0, prefix.length);

			el.textContent = '';
			el.appendChild(span);
			el.appendChild(document.createTextNode(text.slice(prefix.length)));
			el.dataset.cpoPrefixed = 'true';
		});
	}

	function buildCardModel(root) {
		var cardEls = Array.prototype.slice.call(root.querySelectorAll('[data-cpo-card]'));

		return cardEls.map(function (el) {
			var facetsScript = el.querySelector('[data-cpo-facets]');
			var facets = {};

			if (facetsScript) {
				try {
					facets = JSON.parse(facetsScript.textContent);
				} catch (e) {
					facets = {};
				}
			}

			return {
				el: el,
				certification: normalize(facets.certification),
				lengths: (facets.lengths || []).map(normalize)
			};
		});
	}

	function normalize(value) {
		return (value || '').toString().trim().toLowerCase();
	}

	function readActiveState(filterInputs) {
		var state = { certification: new Set(), length: new Set() };

		filterInputs.forEach(function (input) {
			if (!input.checked) return;
			var facet = input.getAttribute('data-cpo-filter');
			if (state[facet]) state[facet].add(normalize(input.value));
		});

		return state;
	}

	// Checkboxes within a facet are OR'd; separate facets are AND'd. A card with no certification value set
	// simply never matches an active certification filter, which is the correct outcome for an untagged bat.
	function cardMatchesState(card, state) {
		if (state.certification.size > 0 && !state.certification.has(card.certification)) return false;

		if (state.length.size > 0) {
			var hasLength = card.lengths.some(function (l) {
				return state.length.has(l);
			});
			if (!hasLength) return false;
		}

		return true;
	}

	// Hide a certification group once every card inside it is filtered out, so its heading goes with it.
	function updateGroupVisibility(root) {
		var groups = Array.prototype.slice.call(root.querySelectorAll('[data-cpo-group]'));

		groups.forEach(function (group) {
			var cardsInGroup = Array.prototype.slice.call(group.querySelectorAll('[data-cpo-card]'));
			var anyVisible = cardsInGroup.some(function (card) {
				return !card.hidden;
			});

			group.hidden = !anyVisible;
		});
	}

	function applyFilters(root, cards, filterInputs, emptyState) {
		var state = readActiveState(filterInputs);
		var anyActive = state.certification.size > 0 || state.length.size > 0;
		var matchCount = 0;

		cards.forEach(function (card) {
			var matches = cardMatchesState(card, state);
			card.el.hidden = !matches;
			if (matches) matchCount++;
		});

		updateGroupVisibility(root);

		if (emptyState) emptyState.hidden = matchCount > 0;

		var clearButton = root.querySelector('[data-cpo-clear]');
		if (clearButton) clearButton.hidden = !anyActive;

		updateDynamicNarrowing(cards, filterInputs, state);
	}

	// For each unchecked option, test whether selecting it — combined with whatever is active in the OTHER
	// facet, deliberately ignoring its own facet since same-facet checkboxes are OR'd and can only add
	// matches — would yield at least one card. Zero matches means the option is disabled rather than
	// clickable into a dead end. Same approach as bbcor-bats-grid-filter.js.
	function updateDynamicNarrowing(cards, filterInputs, liveState) {
		filterInputs.forEach(function (input) {
			var option = input.closest('.usa-bats-grid__filter-option');

			if (input.checked) {
				input.disabled = false;
				if (option) option.classList.remove('is-unavailable');
				return;
			}

			var facet = input.getAttribute('data-cpo-filter');
			var testState = {
				certification: new Set(liveState.certification),
				length: new Set(liveState.length)
			};
			testState[facet] = new Set([normalize(input.value)]);

			var hasMatch = cards.some(function (card) {
				return cardMatchesState(card, testState);
			});

			input.disabled = !hasMatch;
			if (option) option.classList.toggle('is-unavailable', !hasMatch);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();